import type { Document, ProductType } from '@/types'
import { isProductType, getProduct, DEFAULT_PRODUCT_TYPE } from '@/config/products'
import {
  fetchDocumentsFromR2,
  uploadDocument as r2UploadDocument,
  deleteDocument as r2DeleteDocument,
  type DocumentFormData,
} from './r2Service'

type DocumentListener = (documents: Document[]) => void

const DOCUMENTS_JSON_PATH = '/documents.json'
const LOCAL_OVERRIDE_KEY = 'nexgen:documents'
const R2_MODE_KEY = 'nexgen:useR2'

// Check if R2 mode is enabled (defaults to TRUE - can be disabled via env var or localStorage)
const isR2ModeEnabled = (): boolean => {
  // Check environment variable to explicitly disable
  if (import.meta.env.VITE_USE_R2 === 'false') {
    return false
  }
  // Check localStorage setting (for runtime toggle) - if explicitly set to 'false', disable
  if (typeof window !== 'undefined' && localStorage.getItem(R2_MODE_KEY) === 'false') {
    return false
  }
  // Default to TRUE - R2 mode is enabled by default
  return true
}

interface GetDocumentsOptions {
  includeInactive?: boolean
  forceR2?: boolean // Force fetch from R2 even if not in R2 mode
}

class DocumentService {
  private documents: Document[] = []
  private loadingPromise: Promise<Document[]> | null = null
  private listeners = new Set<DocumentListener>()

  private get isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
  }

  private getLocalOverride(): Document[] | null {
    if (!this.isBrowser) return null
    const raw = localStorage.getItem(LOCAL_OVERRIDE_KEY)
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return parsed.map((doc, index) => this.normalizeDocument(doc, index))
      }
    } catch (error) {
      console.error('Failed to parse local document override', error)
    }
    return null
  }

  private persistLocal(documents: Document[]): void {
    if (!this.isBrowser) return
    try {
      localStorage.setItem(LOCAL_OVERRIDE_KEY, JSON.stringify(documents))
    } catch (error) {
      console.error('Failed to persist documents locally', error)
    }
  }

  private async fetchRemoteDocuments(): Promise<Document[]> {
    const response = await fetch(`${DOCUMENTS_JSON_PATH}?v=${Date.now()}`)
    if (!response.ok) {
      throw new Error(`Failed to load documents.json (${response.status})`)
    }
    const data = await response.json()
    const documents = Array.isArray(data) ? data : data.documents
    if (!Array.isArray(documents)) {
      throw new Error('documents.json is not formatted correctly')
    }
    return documents.map((doc: Partial<Document>, index: number) =>
      this.normalizeDocument(doc, index),
    )
  }

  private async fetchR2Documents(): Promise<Document[]> {
    try {
      const documents = await fetchDocumentsFromR2(undefined, true)
      return documents.map((doc: Partial<Document>, index: number) =>
        this.normalizeDocument(doc, index),
      )
    } catch (error) {
      console.error('Failed to fetch documents from R2:', error)
      // Fallback to static documents.json
      console.log('Falling back to static documents.json')
      return this.fetchRemoteDocuments()
    }
  }

  private normalizeDocument(doc: Partial<Document>, index = 0): Document {
    // Preserve any known product (structural-floor, underlayment, structural-wall);
    // fall back to the default only for unknown/empty values.
    const productType: ProductType = isProductType(doc.productType)
      ? doc.productType
      : DEFAULT_PRODUCT_TYPE
    const fallbackFilename =
      doc.filename || this.extractFilenameFromUrl(doc.url) || `document-${index + 1}.pdf`
    const numericSortOrder = Number(doc.sortOrder)
    const sortOrder =
      Number.isFinite(numericSortOrder) && numericSortOrder > 0
        ? numericSortOrder
        : index + 1

    return {
      id: doc.id || `doc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: doc.name || 'Untitled Document',
      description: doc.description || '',
      filename: fallbackFilename,
      url: doc.url || '',
      size: doc.size,
      type: doc.type || 'TDS',
      required: doc.required ?? false,
      products: doc.products || getProduct(productType).sizes,
      productType,
      sortOrder,
      active: doc.active ?? true,
    }
  }

  private extractFilenameFromUrl(url?: string): string | null {
    if (!url) return null
    try {
      const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined)
      const segments = parsed.pathname.split('/')
      const candidate = segments.pop()
      return candidate || null
    } catch {
      const segments = url.split('/')
      return segments.pop() || null
    }
  }

  private async ensureLoaded(forceR2 = false): Promise<Document[]> {
    if (this.documents.length) {
      return this.documents
    }

    if (this.loadingPromise) {
      return this.loadingPromise
    }

    this.loadingPromise = (async () => {
      // Check if R2 mode is enabled (default is TRUE)
      const useR2 = forceR2 || isR2ModeEnabled()

      // Only use local override if R2 mode is DISABLED
      if (!useR2) {
        const override = this.getLocalOverride()
        if (override && override.length) {
          this.documents = override
          return this.documents
        }
      }

      // Fetch from R2 if enabled, otherwise use static JSON
      const remote = useR2
        ? await this.fetchR2Documents()
        : await this.fetchRemoteDocuments()
      this.documents = remote
      return this.documents
    })()

    try {
      return await this.loadingPromise
    } finally {
      this.loadingPromise = null
      this.notifyListeners()
    }
  }

  private notifyListeners(): void {
    const snapshot = this.documents.map(doc => ({ ...doc }))
    this.listeners.forEach(listener => listener(snapshot))
  }

  subscribe(listener: DocumentListener): () => void {
    this.listeners.add(listener)
    if (this.documents.length) {
      listener(this.documents.map(doc => ({ ...doc })))
    }
    return () => this.listeners.delete(listener)
  }

  async getAllDocuments(options: GetDocumentsOptions = {}): Promise<Document[]> {
    const docs = await this.ensureLoaded(options.forceR2)
    const filtered = options.includeInactive ? docs : docs.filter(doc => doc.active)
    return [...filtered].sort((a, b) => a.sortOrder - b.sortOrder)
  }

  async getDocumentsByProductType(
    productType: ProductType,
    options: GetDocumentsOptions = {},
  ): Promise<Document[]> {
    const docs = await this.getAllDocuments(options)
    return docs.filter(doc => doc.productType === productType)
  }

  async createDocument(input: Partial<Document>): Promise<Document> {
    if (!input.name || !input.url || !input.type) {
      throw new Error('Document name, type, and URL are required')
    }
    const docs = await this.ensureLoaded()
    const productType: ProductType = isProductType(input.productType)
      ? input.productType
      : DEFAULT_PRODUCT_TYPE
    const desiredSortOrder = Number(input.sortOrder)
    const fallbackSortOrder =
      Math.max(
        0,
        ...docs.filter(doc => doc.productType === productType).map(doc => doc.sortOrder),
      ) + 1
    const nextSortOrder =
      Number.isFinite(desiredSortOrder) && desiredSortOrder > 0 ? desiredSortOrder : fallbackSortOrder

    const document = this.normalizeDocument(
      {
        ...input,
        productType,
        sortOrder: nextSortOrder,
      },
      docs.length,
    )

    this.documents = [...docs, document]
    this.persistLocal(this.documents)
    this.notifyListeners()
    return document
  }

  async updateDocument(id: string, updates: Partial<Document>): Promise<Document> {
    const docs = await this.ensureLoaded()
    const index = docs.findIndex(doc => doc.id === id)
    if (index === -1) {
      throw new Error('Document not found')
    }

    const updated = this.normalizeDocument(
      {
        ...docs[index],
        ...updates,
        id,
      },
      index,
    )

    this.documents = [
      ...docs.slice(0, index),
      updated,
      ...docs.slice(index + 1),
    ]
    this.persistLocal(this.documents)
    this.notifyListeners()
    return updated
  }

  async deleteDocument(id: string): Promise<void> {
    const docs = await this.ensureLoaded()
    this.documents = docs.filter(doc => doc.id !== id)
    this.persistLocal(this.documents)
    this.notifyListeners()
  }

  async reloadFromRemote(): Promise<void> {
    this.documents = await this.fetchRemoteDocuments()
    this.persistLocal(this.documents)
    this.notifyListeners()
  }

  async exportDocuments(): Promise<void> {
    if (!this.isBrowser) return
    const docs = await this.ensureLoaded()
    const payload = { documents: docs }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'documents.json'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  clearLocalCache(): void {
    if (!this.isBrowser) return
    localStorage.removeItem(LOCAL_OVERRIDE_KEY)
  }

  // =============================================================================
  // R2 STORAGE METHODS
  // =============================================================================

  /**
   * Check if R2 mode is currently enabled
   */
  isUsingR2(): boolean {
    return isR2ModeEnabled()
  }

  /**
   * Enable or disable R2 mode (persists to localStorage)
   * Note: R2 mode is enabled by default, so we only store 'false' to disable
   */
  setR2Mode(enabled: boolean): void {
    if (!this.isBrowser) return
    if (enabled) {
      // Remove the key to use the default (true)
      localStorage.removeItem(R2_MODE_KEY)
    } else {
      // Explicitly set to 'false' to disable
      localStorage.setItem(R2_MODE_KEY, 'false')
    }
    // Clear cached documents to force reload
    this.documents = []
    this.loadingPromise = null
  }

  /**
   * Upload a document file to R2 and create metadata
   */
  async uploadDocumentToR2(
    file: File,
    metadata: DocumentFormData
  ): Promise<{ success: boolean; document?: Document; error?: string }> {
    const result = await r2UploadDocument(file, metadata)

    if (result.success && result.document) {
      // Refresh the documents list
      this.documents = []
      this.loadingPromise = null
      await this.ensureLoaded(true)
      this.notifyListeners()
    }

    return result
  }

  /**
   * Delete a document from R2 storage
   */
  async deleteDocumentFromR2(documentId: string): Promise<{ success: boolean; error?: string }> {
    const result = await r2DeleteDocument(documentId)

    if (result.success) {
      // Remove from local cache
      this.documents = this.documents.filter(doc => doc.id !== documentId)
      this.notifyListeners()
    }

    return result
  }

  /**
   * Reload documents from R2
   */
  async reloadFromR2(): Promise<void> {
    this.documents = await this.fetchR2Documents()
    this.notifyListeners()
  }

  /**
   * Sync local changes to R2 (for migration purposes)
   * This would upload all locally-stored documents to R2
   */
  async syncToR2(): Promise<{ success: number; failed: number; errors: string[] }> {
    const results = { success: 0, failed: 0, errors: [] as string[] }

    // Get current local documents
    const localDocs = this.getLocalOverride()
    if (!localDocs || localDocs.length === 0) {
      return results
    }

    // Note: This method would need actual file data to upload
    // It's a placeholder for migration scenarios where you have file data
    console.warn('syncToR2: Document files must be uploaded individually with their file data')

    return results
  }

}

export const documentService = new DocumentService()

// Re-export R2 service utilities for convenience
export {
  uploadDocument as uploadDocumentToR2,
  deleteDocument as deleteDocumentFromR2,
  fetchDocumentsFromR2,
  getDocumentPreviewUrl,
  getCoverPageStatus,
  uploadCoverPage,
  getCoverPagePreviewUrl,
  formatFileSize,
  validatePdfFile,
} from './r2Service'

